import { Checkbox, Grid, Stack, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setIsReloadTableData } from 'common/store/reducer/promptBuilderReducer';

import { getPromptLocation, setDefaultPrompt } from '../../api';
import { TookKitOptions } from '../../api/type-def';
import { PROMPT_DEFAULT_MESSAGES } from '../../PromptBuilderDetails/PromptBuilderConstants';
import { modifiedToolkitOptions } from '../../PromptBuilderUtils';

import actionStyles from '../PromptActions.module.scss';

import styles from './PromptDefault.module.scss';

interface IPromptDefault {
	closeDefaultModal: () => void;
}
const PromptDefault = (props: IPromptDefault) => {
	const dispatch = useDispatch();

	const { closeDefaultModal } = props;

	const { OK } = HTTP_STATUS_CODES;

	const { DEFAULT_INFO, DEFAULT_WARNING, NO_LOCATIONS_MSG } =
		PROMPT_DEFAULT_MESSAGES;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [locationsLoader, setLocationsLoader] = useState<boolean>(false);

	const selectedPromptData: any = useSelector(
		(state: RootState) => state.promptBuilder.selectedPrompt,
	);

	const [checkedStates, setCheckedStates] = useState<
		Record<string, boolean | 'intermediate'>
	>({});
	const [openDialog, setOpenDialog] = useState<string | null>(null); // Track dialog per option
	const [toolKitOptions, setToolKitOptions] = useState<TookKitOptions[]>([]);

	const handleChange = (key: string, existingDefault: boolean) => {
		setCheckedStates((prev) => {
			const updatedStates = { ...prev };

			// If another dialog is open, reset its state and close the dialog
			if (openDialog && openDialog !== key) {
				updatedStates[openDialog] = false; // Reset previously open checkbox state
				setOpenDialog(null);
			}

			// Handle the checkbox click
			const currentChecked = updatedStates[key];

			if (existingDefault) {
				// If existing_default is true, retain the existing behavior with intermediate state
				if (!currentChecked) {
					setOpenDialog(key);
					updatedStates[key] = 'intermediate';
				} else if (currentChecked === true) {
					updatedStates[key] = false;
					setOpenDialog(null); // Close dialog if checkbox is unchecked
				}
			} else {
				// If existing_default is false, directly enable/disable the checkbox
				updatedStates[key] = !currentChecked;
			}

			// Remove unchecked items to ensure clean state
			if (!updatedStates[key]) {
				delete updatedStates[key];
			}

			return updatedStates;
		});
	};

	const handleDialogResponse = (response: boolean, key: string) => {
		if (response) {
			setCheckedStates((prev) => ({ ...prev, [key]: true }));
		} else {
			setCheckedStates((prev) => ({ ...prev, [key]: false }));
		}
		setOpenDialog(null);
	};

	const getLocations = async () => {
		setLocationsLoader(true);
		const response: any = await getPromptLocation(
			selectedPromptData?.ai_model_key,
		);

		if (response?.statusCode === OK) {
			const locationList = response.responseBody.data;
			setLocationsLoader(false);
			return locationList;
		}
		dispatch(
			setToastMessage({
				message: 'Error while fetching locations',
				severity: 'error',
				isToastOpen: true,
			}),
		);
		setLocationsLoader(false);
		return {};
	};

	useEffect(() => {
		const fetchLocationDetails = async () => {
			if (selectedPromptData?.ai_model_key) {
				const locationDetails = await getLocations();
				if (locationDetails.length > 0) {
					setToolKitOptions(modifiedToolkitOptions(locationDetails));
				}
			}
		};

		fetchLocationDetails(); // Call the async function
	}, [selectedPromptData]);

	const handleDefaultAction = async () => {
		setIsLoading(true);
		const payload = {
			is_active: true,
			key: selectedPromptData.key,
			locations: Object.keys(checkedStates).filter((key) => checkedStates[key]),
		};
		const res = await setDefaultPrompt(payload);
		if (res?.statusCode === OK) {
			setIsLoading(false);
			dispatch(
				setToastMessage({
					message: `Successfully changed the default prompt`,
					severity: 'success',
					isToastOpen: true,
				}),
			);
			dispatch(setIsReloadTableData(true));
			closeDefaultModal();
		} else {
			const { errorBody } = res;
			const responseError: any = res.error;
			const errorMessage =
				(errorBody?.error as string) || (responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setIsLoading(false);
		}
	};

	const isSaveDisabled = () =>
		// Check if there are any selected checkboxes
		toolKitOptions?.length === 0 ||
		!Object.values(checkedStates).some((value) => value === true) ||
		false;

	return (
		<Grid
			container
			direction="column"
			spacing={2}
			sx={{
				width: '100%',
				boxSizing: 'border-box',
				maxWidth: '100%', // Restrict width
				overflow: 'hidden', // Prevent overflow
			}}
		>
			<Stack
				className={actionStyles['prompt-modal-title-container']}
				onClick={(event) => event.stopPropagation()}
				data-testid="title-container"
			>
				<Grid item>
					<Typography className={actionStyles['prompt-modal-title']}>
						Set as default
					</Typography>
				</Grid>
				<CloseIcon
					fontSize="medium"
					onClick={closeDefaultModal}
					className={actionStyles['prompt-modal-close']}
					data-testid="close-icon"
				/>
			</Stack>
			<Grid item>
				<Typography className={actionStyles['prompt-modal-warning']}>
					{DEFAULT_INFO}
				</Typography>
			</Grid>
			<Grid item>
				<div>
					{locationsLoader ? (
						<Loader show dataTestId="locationLoader" />
					) : (
						<Stack>
							{toolKitOptions?.length > 0 ? (
								toolKitOptions.map((option) => (
									<Stack
										direction="row"
										alignItems="center"
										spacing={1}
										key={option.key}
									>
										<Checkbox
											disableRipple
											checked={checkedStates[option.key] === true}
											indeterminate={
												checkedStates[option.key] === 'intermediate'
											}
											onChange={() =>
												handleChange(option.key, option.existing_default)
											}
										/>
										<Typography
											variant="body2"
											className={styles['option-style']}
										>
											{option.label}
										</Typography>
										{openDialog === option.key && (
											<Stack
												direction="row"
												alignItems="center"
												className={styles['custom-confirmation-box']}
											>
												<Typography className={styles['text-style']}>
													{DEFAULT_WARNING}
												</Typography>
												<ButtonComp
													clickHandler={() =>
														handleDialogResponse(true, option.key)
													}
													btnType="text"
													compClass={styles['btn-style']}
													autoFocus
												>
													Yes
												</ButtonComp>
												<ButtonComp
													clickHandler={() =>
														handleDialogResponse(false, option.key)
													}
													compClass={styles['btn-style']}
													btnType="text"
												>
													No
												</ButtonComp>
											</Stack>
										)}
									</Stack>
								))
							) : (
								<Typography>{NO_LOCATIONS_MSG}</Typography>
							)}{' '}
						</Stack>
					)}
				</div>
			</Grid>
			<Grid item>
				<Grid container justifyContent="flex-start" spacing={2}>
					<Grid item>
						<ButtonComp
							btnType="secondary"
							onClick={closeDefaultModal}
							data-testid="cancel-dialog-btn"
						>
							Cancel
						</ButtonComp>
					</Grid>
					<Grid item>
						<ButtonComp
							btnType="primary"
							loading={isLoading}
							disabled={isSaveDisabled()}
							onClick={handleDefaultAction}
							data-testid="save-default-btn"
						>
							Save
						</ButtonComp>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default PromptDefault;
